/**
 * The guest pending-question claim: what this tab is holding, and its cross-tab mirror.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: split out of `useClaimPendingQuestions` so
 * the storage keys exist once and so the door screen can mirror them without
 * importing that hook's axios/react-query graph. The hook still owns the claim
 * itself (the POST); this module owns WHERE the claim lives, on every one of the
 * three stores it lives in -- this tab's sessionStorage, the profile-wide
 * localStorage mirror, and native Preferences.
 *
 * WHAT IT HOLDS IS NO LONGER A BARE FLAG, and that is the security change.
 * `POST /questions/pending` is unauthenticated and stores a client-supplied
 * email, so a row says nothing about who wrote it; the claim used to publish
 * every unclaimed row filed under the signing-in address, and a bare "there is
 * one waiting" was the whole of what had to travel. A third party could
 * therefore plant a row for victim@, ask for a genuine link to victim@ carrying
 * the flag, and the victim's content-blind Continue published the planted text
 * under their name. No credential closes that -- the attacker is also a
 * legitimate requesting device -- so the defence is INFORMED CONSENT: the
 * landing gate names exactly what Continue will publish, and the claim publishes
 * nothing else. The per-row `claim_secret` the create response serves once is
 * what makes "nothing else" enforceable; the title and the ask time are what
 * make the gate's promise checkable.
 *
 * Deliberately a NEAR-LEAF module: `@utils/magicLinkMirror`, `@utils/logger`,
 * `@lib/platform` and `@lib/magicLink/firstSendAddress` (itself a leaf, for the one
 * address normalisation the door and this store share), plus a dynamic
 * `@capacitor/preferences` import. The reason `useClaimPendingQuestions` cannot own
 * the storage is axios and react-query, and none of these five drag either in.
 */
import { normalizeSendAddress } from '@lib/magicLink/firstSendAddress';
import { isNativePlatform } from '@lib/platform';
import { logger } from './logger';
import {
  MAGIC_LINK_MIRROR_KEYS,
  readMagicLinkMirror,
  writeMagicLinkMirror,
  writeMagicLinkPayloadMirror,
} from './magicLinkMirror';

/**
 * Legacy name: renaming would orphan sessionStorage entries a tab already wrote under the
 * previous bundle mid-deploy. `storageCleanup.ts` imports this constant, so its sweep tracks
 * the value automatically rather than keeping its own copy of the string.
 */
export const PENDING_QUESTION_KEY = 'intended_action';
export const PENDING_QUESTION_VALUE = 'create_pending_question';

/**
 * WHICH QUESTIONS this tab's claim would publish: one entry per guest question, each
 * naming the row it may publish and enough to describe it on a gate.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: a SECOND key beside `PENDING_QUESTION_KEY` rather than an
 * envelope replacing it, and the reason is the deploy, not a reader. The flag keeps its legacy key
 * and value (the "Legacy name" note above), so a tab the previous bundle armed still reads as a
 * claim, and the entries went in beside it so that read did not change. No production module
 * outside this one reads the value: the sign-out sweep in `storageCleanup.ts` removes these keys
 * by name and its account-switch handover skips them by name; `useSignInOut` asks
 * `hasPendingQuestionClaimInTab` (flag OR entries), and the settle ladder and SignupFlow's claim
 * ask `hasPendingQuestionClaim`, which is that plus native; `/auth/link` asks
 * `readUnnamedPendingQuestionClaimInTab`; and native keeps a marker of its own, written from the
 * constant and compared by `hasNativePendingQuestionClaim`.
 *
 * The two are independent facts. A row created before the backend minted secrets answers
 * `claim_secret: null`, and that guest's same-browser claim still has to work off the device
 * token, so the flag can stand with no entries: that rules out the list BEING the flag. An
 * envelope with an unnamed field beside the list could hold both, at the price of the legacy
 * read. The other two per-tab keys, `PENDING_QUESTION_SOURCE_KEY` and
 * `PENDING_QUESTION_DEFERRED_KEY`, are separate for the reason each one's note gives: each is a
 * fact a tab can hold at the same time as the others, and one key holds one fact.
 *
 * What four keys cost is that four writes are kept in step by ordering rules rather than made as
 * one. The notes on `PENDING_QUESTION_DEFERRED_KEY`, on the provenance bit in `armClaimInTab` and
 * on `discardClaimInTab` record three bugs the layout has produced so far, and the arm's read-back
 * checks the flag and the entries but not the delivery bit (see `PENDING_QUESTION_SOURCE_KEY`).
 */
export const PENDING_QUESTION_CLAIMS_KEY = 'intended_action_claims';

/**
 * WHERE THIS TAB'S CLAIM CAME FROM, and the only thing that reads it is the mirror
 * writer.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the hydrate-then-drop below binds a mirrored claim
 * to the flow that wrote it, and the DOOR was handing that back one hop later. A link
 * Logto rejects (`token_expired`, `token_consumed`, `email_mismatch`) bounces to `/auth`
 * IN THIS TAB, and the door mirrors whatever the tab holds -- on mount and again on
 * every send. For a claim this tab armed itself, or took delivery of from this
 * profile's own mirror, that is the retry path working as designed. For a claim the
 * WIRE delivered it is a promotion: a claim carried cross-device, from a browser
 * profile this one has nothing to do with, offered to the next link redeemed here.
 * A separate key rather than a second value under the existing one, because one key
 * cannot hold two facts a tab can hold at once: a tab that committed a delivered claim
 * and then asks a secret-less question of its own carries the delivery AND the unnamed
 * flag. The deferred marker's note gives the same reason for its own key.
 *
 * THE ARM'S READ-BACK DOES NOT CHECK THIS BIT, only the flag and the entries. A write
 * of it that THROWS still fails closed, because it sits inside the arm's `try` with the
 * others. What escapes is storage accepting the other writes and silently discarding
 * this one alone, which no known failure does: eviction and partitioning act on the
 * whole origin, and then the entries read-back fails too. Lost, the bit would let the
 * door mirror a delivered claim profile-wide.
 */
export const PENDING_QUESTION_SOURCE_KEY = 'intended_action_source';
const PENDING_QUESTION_SOURCE_DELIVERY = 'magic_link_delivery';

/**
 * THE DEFERRED CLAIM: "a claim may still be owed here, but only a GATE may spend it."
 *
 * ITS OWN KEY, and it started as a third value on `PENDING_QUESTION_SOURCE_KEY`. That was wrong
 * as soon as the COMBINED state became reachable -- named entries plus a deferred unnamed scope --
 * because one key cannot hold two facts a tab genuinely holds at once, and the collisions were
 * silent in both directions: a mirror hydration removed the source key and destroyed the deferred
 * marker before the gate ever read it (stranding those rows to expiry), and the promotion wrote
 * over the DELIVERY bit, so a door revisit mirrored delivered entries profile-wide -- the exact
 * cross-person bleed that bit exists to prevent. Separate facts, separate keys; neither writer
 * can now clobber the other's.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: it exists because of what sparing the legacy marker after a
 * secrets success exposed. `hasPendingQuestionClaim`
 * is read by `useCallbackRedirect`'s UNCONDITIONAL claim on every `/callback`, which has no
 * consent step of any kind -- the gate lives on `/auth/link` alone. So a raw flag left standing
 * after a consented secrets claim would be spent by the next ORDINARY sign-in in this tab, with
 * nothing naming what it published.
 *
 * ITS OWN KEY IS WHAT MAKES THAT IMPOSSIBLE BY CONSTRUCTION, and the mechanism is the reader
 * side rather than the writer side: the two ungated ladders ask `hasPendingQuestionClaimInTab`
 * (the raw flag OR named entries, plus native Preferences behind it), and NEITHER limb reads this
 * key. So a deferred claim answers false to both of them, and the ordinary path claims nothing at
 * all -- exactly what it did before any of this. `readDeferredPendingQuestionClaimInTab` is the
 * only reader, `/auth/link` is its only caller, and a Continue there PROMOTES the marker to a real
 * flag. Consent is what turns a deferred claim back into a claim. `leaves the ordinary callback
 * path with nothing to claim after a secrets success` is the assertion that holds the exemption
 * in place -- it exercises the ladder, so no module can route around it.
 *
 * Per-tab and never mirrored, so it cannot reach another tab, profile or device; swept on
 * sign-out with the other per-tab claim keys: by the signing-out tab's own sweep
 * (`PENDING_QUESTION_TAB_KEYS`, spread into `storageCleanup.ts`'s sign-out list), and in every
 * other tab of the profile by `AuthProvider`'s `handleSignedOutInOtherTab` (S-02 of #1279's
 * review).
 */
export const PENDING_QUESTION_DEFERRED_KEY = 'intended_action_deferred';
const PENDING_QUESTION_DEFERRED_VALUE = 'deferred_unnamed_claim';

/**
 * EVERY per-tab key this claim lives in, in one place, so the sweeps track the set by construction
 * rather than by three hand-maintained copies of it.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the four keys were hand-listed in `clearPendingQuestionClaimInTab`
 * and twice in `storageCleanup.ts`, and this feature added two of them to each list by hand. The
 * mirrors next door are covered by construction (`...Object.values(MAGIC_LINK_MIRROR_KEYS)`) and
 * `storageCleanup.ts` already records this exact drift biting once, with `FIRST_SEND_ADDRESS_KEY`
 * in one list and not the other. A JUDGED SUBSET still gets written out by hand -- the mid-flight
 * handover exemption and `discardClaimInTab` are both subsets on purpose, and a spread there would
 * hide the judgement.
 */
export const PENDING_QUESTION_TAB_KEYS = [
  PENDING_QUESTION_KEY,
  PENDING_QUESTION_CLAIMS_KEY,
  PENDING_QUESTION_SOURCE_KEY,
  PENDING_QUESTION_DEFERRED_KEY,
] as const;

/**
 * The DURABLE half of the claim marker: Capacitor Preferences survives the app being
 * killed mid-auth, which sessionStorage does not. Exported because it is the key a
 * native test has to name to tell a kept marker from a retired one.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: moved here from `useClaimPendingQuestions` with the
 * rest of the storage layer, so the create site can arm every store this claim lives in
 * through ONE call. The marker key keeps its exact value and meaning -- an app killed
 * mid-auth on the previous bundle still reads back as "a claim is pending" -- and the
 * entries are a second, additive key beside it.
 */
export const NATIVE_PENDING_QUESTION_KEY = 'meetball_pending_question';
export const NATIVE_PENDING_QUESTION_CLAIMS_KEY = 'meetball_pending_question_claims';

/**
 * One guest question a claim would publish.
 *
 * `secret` is the `claim_secret` the create response served ONCE (only its SHA-256 is
 * stored server-side), and it is what lets a claim name this row instead of sweeping
 * every row filed under an address. It is NOT an authorship proof -- whoever created the
 * row holds its secret, attacker included -- which is why `title` and `askedAt` travel
 * with it: the gate has to be able to say what Continue publishes and when it was asked,
 * so the visitor can recognise their own question or fail to.
 */
export interface PendingQuestionClaimEntry {
  secret: string;
  /**
   * Plain text: the server's own gate string for the row (`stripServedClaimTitle`), or the one the
   * client derives when it has none (`clampClaimTitle`). Rendered as TEXT, never as markup.
   */
  title: string;
  /** The row's creation time, ISO-8601. Rendered as relative time on the gate. */
  askedAt: string;
  /**
   * The address the row was filed under, trimmed and lower-cased (`normalizeSendAddress`), and the
   * one fact that decides where this entry may travel: the door carries it only in a send to that
   * same address (`claimEntriesForRecipient`). Set by the guest submission (the address the guest
   * typed, which the create files the row under), by `/auth/link`'s delivered commit (the
   * address the link was minted for, the one mailbox the secret has already reached), and by the
   * door's cold resume, which files a carried delivery under the claim's `login_hint`, the same
   * address for the same reason (`AuthDoor.tsx`, #1261).
   *
   * OPTIONAL because an entry stored before it existed has none. Such an entry keeps the
   * first-recipient binding instead, for as long as the flow lives (30 minutes).
   *
   * NEVER ON THE WIRE. The door uploads secrets (`claimSecretsForRequest`), the claim posts
   * secrets, and no log line prints an entry, so the address stays on this device.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: no new kind of personal data at rest (review 5330705557, R07).
   * The tab already holds the address it first sent to (`FIRST_SEND_ADDRESS_KEY`), and so does its
   * mirror. This sits beside the entry in those same two places and is swept with it: in the tab
   * for as long as the entry is there (sessionStorage, which has no clock of its own: the entry
   * goes when the claim settles or is refused, at sign-out, or with the tab), and in the mirror
   * under its 30-minute TTL. NOT IN NATIVE PREFERENCES: that store has no TTL (TECH-485), outlives
   * an app kill, and nothing that reads this field reads it, so `storeNativePendingQuestionClaim`
   * writes each entry without it.
   */
  filedUnder?: string;
}

/**
 * How long a title the CLIENT DERIVES may be (`clampClaimTitle`), in code points: the clamp a
 * backend without #920 applies to its own gate title, which is the only backend a derived title
 * stands in for. Long enough to recognise your own question, short enough that a wall of attacker
 * text cannot be the page.
 *
 * NOT THE BOUND ON A STORED OR SERVED TITLE. The server bounds what it serves by what a reader can
 * see (backend #920), and the store applies only `CLAIM_TITLE_CEILING`, so a served title longer
 * than this is kept whole.
 */
const CLAIM_TITLE_MAX_LENGTH = 80;
const ELLIPSIS = '\u2026';

/**
 * How many questions one claim may carry, matching the backend's
 * `PENDING_QUESTION_CLAIMS_MAX` (`app/schemas/magic_link.py`, where the request field carries it
 * as `max_length`). ONE NUMBER FOR BOTH BOUNDS: what the store may hold and what a request may
 * send, so the gate can never name a question Continue cannot publish.
 *
 * ENFORCED CLIENT-SIDE, and that is not belt-and-braces. Over the limit the magic-link
 * request does not fail: 6-32 entries make the backend DROP the claims half while keeping
 * the intent and the destination, and 33 or more make it drop the WHOLE context including
 * a valid redirect path -- both behind the endpoint's invariant 202, so the client is told
 * nothing either way. A cap here is the only thing that keeps a legitimate 6th question
 * from silently costing the visitor their other five AND their destination.
 */
export const PENDING_QUESTION_CLAIMS_MAX = 5;

/**
 * The two `Cf` characters that survive the strip below: ZERO WIDTH NON-JOINER (U+200C) and
 * ZERO WIDTH JOINER (U+200D).
 *
 * A KEEP-LIST rather than a drop-list, matching the backend's `claim_titles` module, so a
 * format character nobody has thought about yet is dropped by default. Why these two are kept
 * is the reason the rest go: the strip exists for text that RENDERS as something other than
 * what it says (a bidi override reorders the reader's view without changing a byte), and a
 * joiner cannot do that -- it only binds adjacent glyphs. Dropping the whole category took
 * every family emoji apart into its component people on an ordinary write path, and bought
 * nothing against the attack the category is screened for.
 */
// ESCAPED, NOT LITERAL, and that is the point: written with the raw characters this line
// renders as `new Set(['', ''])` in every editor, diff and review view, so the one list that
// decides which format characters reach the consent gate could not be read -- and swapping in
// another invisible (U+2060, U+200B) would not show up in review at all.
const KEPT_FORMAT_CHARACTERS = new Set(['\u200C', '\u200D']);

const CONTROL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u;
const WHITESPACE = /\s/u;

/**
 * `title` as one plain line: the dangerous invisibles removed, whitespace collapsed.
 *
 * Removal rather than replacement, so nothing a removed character could have joined is spaced
 * apart by its going. May return the empty string -- a title that was nothing but invisibles
 * has no content, and each caller decides what to do about that.
 */
const normaliseClaimTitle = (title: string): string =>
  Array.from(title)
    .map(character => {
      if (KEPT_FORMAT_CHARACTERS.has(character)) return character;
      if (/\s/u.test(character)) return ' ';
      return CONTROL_OR_FORMAT.test(character) ? '' : character;
    })
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();

/**
 * `title` as one plain line of at most `CLAIM_TITLE_MAX_LENGTH` characters: a title the CLIENT
 * derives, for the one place it has no server copy to show.
 *
 * OURS, AND DELIBERATELY NOT A PORT of the backend's `clamp_title` -- its own module states
 * that client parity is not required and should not be restored. It is not achievable anyway
 * (the server's window counts what a reader can see by its own tables, and the two whitespace
 * definitions differ), and it is not needed: the preview -> commit -> claim join is on the
 * SECRET and never on the title, so a cosmetic divergence cannot mis-join anything.
 *
 * NEVER APPLIED TO A TITLE THE SERVER SERVED. It used to run over every delivered title as well,
 * and for those it was a second cut below the server's own bound: the server bounds the title
 * the gate serves by what a reader can see (backend #920), so 78 joiners and then "Who can help
 * me move?" came back whole and rendered here as a lone "W" and an ellipsis while all of it
 * published. A served title goes through `stripServedClaimTitle` alone. The one caller is
 * `guestAskClaimTitle`, for a create response that carried no `claim_title`.
 *
 * ESCAPING IS NOT ITS JOB -- the gate renders these as React text children, so React escapes
 * them.
 */
export const clampClaimTitle = (title: string): string =>
  boundClaimTitleLength(normaliseClaimTitle(title));

/**
 * A SERVED title as the gate renders it: the flow read's `pending_question_claims[].title` and the
 * create response's `claim_title`, which the server computed and the client does not re-derive.
 *
 * THE STRIP AND NOTHING ELSE: `normaliseClaimTitle`'s control-and-format strip, joiners kept,
 * whitespace collapsed. A boundary-control defence rather than a rewrite: a served title has
 * already been through the server's own `normalise_title`, which does the same work, so on a
 * served title the strip is the identity and the gate shows exactly what the server chose. What
 * it removes is the assumption that it always has: a bidirectional override in
 * server-round-tripped client input must not reach the pre-auth DOM, where the rendered title
 * would differ from the text that publishes. `strips a bidirectional override from a delivered
 * title` holds that half.
 *
 * NO BOUND AND NO JUDGEMENT. The server bounds a served title (backend #920) and refuses or leaves
 * out one that shows nothing, so the client neither cuts it below the server's bound nor asks
 * again whether it names anything: a second answer by a definition that is not the server's
 * dropped questions the server had named. `CLAIM_TITLE_CEILING`, applied by the store, is the
 * only bound left.
 *
 * ITS OWN FUNCTION rather than a call to `normaliseClaimTitle`, so that if a served title is ever
 * found on which the strip is not the identity, it can narrow to a bidi-control strip here
 * without touching the derived path.
 */
export const stripServedClaimTitle = (title: string): string => normaliseClaimTitle(title);

/**
 * Characters that occupy no ink, so they cannot be part of what a reader SEES.
 *
 * `Default_Ignorable_Code_Point` is the Unicode property for exactly this class, and it covers
 * both joiners as well as every blank-rendering filler a count of the normalised string would
 * otherwise treat as content: U+3164 HANGUL FILLER, U+115F, U+FFA0, U+034F, U+17B4 and the
 * variation selectors. U+2800 BRAILLE PATTERN BLANK is added by hand because it is NOT in that
 * property -- it is an ordinary printable code point whose glyph is empty.
 *
 * WHY THIS IS THE COUNT AND NOT THE STRIP: none of these can make text render as something
 * other than what it says, so `clampClaimTitle` has no reason to remove them and an author's
 * title is never rewritten. What they can do is pass a FLOOR, which is a different question --
 * see `visibleClaimTitleLength`.
 */
const INVISIBLE_TO_A_READER = /[\p{Default_Ignorable_Code_Point}\u2800]/u;

/** Nonspacing and enclosing marks (Mn, Me): each sits on the character before it. */
const NONSPACING_OR_ENCLOSING_MARK = /[\p{Mn}\p{Me}]/u;

/**
 * Whether a reader sees `character` as TEXT: the ONE client definition of a visible claim-title
 * character, and the client's copy of the claim-title contract's rule 5 (backend #920, `_seen` in
 * `app/utils/claim_titles.py`). A character is visible when it is none of these: whitespace,
 * `INVISIBLE_TO_A_READER`, or a nonspacing or enclosing mark. The product owner ruled marks out on
 * 2026-09-29 ("stop counting marks"), so a letter carrying a stack of marks is one character, and a
 * short title that reaches five only by counting its marks is refused.
 *
 * A SPACING MARK (Mc) STAYS VISIBLE: it takes room of its own and the server counts it, and a floor
 * that counted fewer characters than the server would refuse titles the server takes.
 *
 * Asked of the NORMALISED title (`normaliseClaimTitle`), where the control and format characters
 * are already gone apart from the two joiners, which `INVISIBLE_TO_A_READER` holds.
 */
const isVisibleClaimTitleCharacter = (character: string): boolean =>
  !WHITESPACE.test(character) &&
  !INVISIBLE_TO_A_READER.test(character) &&
  !NONSPACING_OR_ENCLOSING_MARK.test(character);

/**
 * How many characters of `title` a reader SEES, counted the way the server counts them
 * (`served_title(...).visible`, backend #920): one for each visible character
 * (`isVisibleClaimTitleCharacter`), and one for a run of whitespace only where it falls between two
 * of them. So `a b c` counts five, and five spaces kept apart by joiners count nothing.
 *
 * FOR CHECKING ONLY -- nothing stores or serves what this counts, and no write path rewrites an
 * author's title.
 *
 * THE GUEST SUBMISSION'S PRE-SUBMIT FLOOR. The create refuses a title whose served string shows
 * fewer than five visible characters, and that 422 lands in the guest catch as generic copy the
 * visitor cannot act on, so the form asks first. It must never refuse a title the server takes,
 * which is why it counts the gaps the server counts and a typed U+2026 as one character. Naming
 * (`claimTitleNamesAnything`) sets both aside: a different question over the same characters.
 *
 * WHERE THE FLOOR AND THE SERVER STILL PART, each accepted:
 * - THE WINDOW. The server counts its SERVED string, the title cut to its 80-character window, and
 *   this counts the whole title, so a title padded with 80 or more charged characters before its
 *   text passes here and meets a 422 there. That is adversarial input, and a whole title never
 *   shows fewer visible characters than its served prefix, so the floor stays the looser side.
 * - UNICODE VERSIONS. `\p{Mn}`, `\p{Me}` and `\p{Default_Ignorable_Code_Point}` follow the
 *   runtime's own tables (the browser's ICU; Node's in tests), newer than the server's UCD 15.1.0.
 *   A code point unassigned in 15.1 that a later version makes a mark is counted out here and
 *   counted by the server, so for that character the floor is the stricter side.
 * - WHITESPACE. The server's (`str.isspace`) takes in U+001C to U+001F and U+0085, which the
 *   normaliser removes as control characters, so a gap made of one of them alone counts nothing
 *   here: stricter by one. U+FEFF is whitespace here and a removed format character there: looser
 *   by one. Both belong to #1179, the normaliser's divergence from the server's.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: no numeral here on purpose. A count of call sites is a claim
 * about the rest of the codebase with nothing that fails when one is added, and this sentence has
 * already gone stale twice -- name the kind of caller instead.
 */
export const visibleClaimTitleLength = (title: string): number => {
  let visible = 0;
  let isGapPending = false;
  for (const character of normaliseClaimTitle(title)) {
    if (WHITESPACE.test(character)) {
      isGapPending = visible > 0;
    } else if (isVisibleClaimTitleCharacter(character)) {
      visible += isGapPending ? 2 : 1;
      isGapPending = false;
    }
  }
  return visible;
};

/**
 * Whether `title` NAMES anything: whether a reader can see a character of it that is not U+2026.
 *
 * THE GATE'S QUESTION, asked only of a title the client derives itself, and never of one the
 * server served (the server answers it for those, backend #920) nor by the store: by
 * `guestAskClaimTitle`, of the title a guest submission derives when the create served none, and
 * by `boundClaimTitleLength`, of its own truncated prefix, which is the same question about a
 * value one step later. Under
 * title-only consent a gate that says `post this ""` is consent over nothing at all, and
 * one that quotes a bare ellipsis is the same consent with a mark in it.
 *
 * AN ELLIPSIS IS NEVER THE TEXT: it says there was more, and it is what a clamp appends when it
 * cuts. The server's clamp appends one when it cuts a title to the gate window (backend #895), so
 * 79 HANGUL FILLERs and then text arrive as the fillers and an ellipsis; counted as visible, that
 * ellipsis kept an entry the gate named with nothing but the mark, while the same fillers without
 * it were refused. Asked of the VISIBLE characters rather than of the string's last one, so an
 * invisible after the ellipsis cannot hide it, and a title of nothing but ellipses names nothing
 * either. A title with anything visible before its ellipsis still names that text.
 *
 * WHITESPACE NAMES NOTHING EITHER. A space is a gap between two things a reader sees, never a
 * thing itself, and the strip collapses a run of spaces but not spaces kept apart by joiners: so
 * joiner-and-space padding came through as a row of spaces, each one counted as something a
 * reader could see, and a title of nothing but that padding named something. Cut at 80 code
 * points, that padding in front of the real text was stored with an ellipsis and quoted on the
 * gate as a blank.
 *
 * NOR DOES A MARK (Mn, Me), which sits on a character and is never one: both rules come from the
 * one definition of a visible character (`isVisibleClaimTitleCharacter`), which the create floor
 * reads too.
 *
 * NOT THE CREATE FLOOR, which counts with `visibleClaimTitleLength`: a minimum length for what a
 * visitor types is a separate rule, and there a typed U+2026 counts, as it does on the server.
 */
export const claimTitleNamesAnything = (title: string): boolean =>
  Array.from(normaliseClaimTitle(title)).some(
    character => character !== ELLIPSIS && isVisibleClaimTitleCharacter(character),
  );

/**
 * `title` cut to `CLAIM_TITLE_MAX_LENGTH` CODE POINTS, and nothing else touched.
 *
 * THE BOUND OF THE CLIENT'S OWN CLAMP (`clampClaimTitle`), separate from the normalisation there
 * because it is the half that cuts. It is not the store's bound: the store holds titles the server
 * served, which it bounds by a window of what a reader can see rather than by code points, so the
 * store applies only `CLAIM_TITLE_CEILING`. Code points, not UTF-16 units, so an emoji is never cut
 * into a lone surrogate.
 */
export const boundClaimTitleLength = (title: string): string => {
  const characters = Array.from(title);
  if (characters.length <= CLAIM_TITLE_MAX_LENGTH) {
    return title;
  }
  const kept = characters
    .slice(0, CLAIM_TITLE_MAX_LENGTH - 1)
    .join('')
    .replace(/\s+$/u, '');
  // NO ELLIPSIS OVER NOTHING, and this is a correctness rule rather than a typographic one. The
  // ellipsis asserts "there was more text here"; appended to a prefix a reader cannot see it
  // becomes the ONLY visible character in the result. So a title whose visible half sits beyond
  // the bound -- 100 joiners then `abcde` -- became 79 joiners plus an ellipsis, a bare `...` on
  // the gate. Returning the bare prefix keeps such a title invisible, which is what the caller's
  // judgement (`claimTitleNamesAnything`) then refuses. Asked as that same question, not as a list
  // of which characters happen to be invisible, so a later addition to the keep-list cannot reopen
  // it and the bound and its caller cannot disagree about what nothing is.
  //
  // DEFENCE IN DEPTH, NOT THE ONLY GUARD: the judgement counts no ellipsis as naming anything, so it
  // refuses 79 joiners plus a manufactured one on its own. `does not manufacture an ellipsis over a
  // prefix with nothing visible in it` pins the rule itself.
  return claimTitleNamesAnything(kept) ? kept + ELLIPSIS : kept;
};

/**
 * THE TITLE A GUEST'S OWN ASK STORES for its claim entry, which is what the gate shows on the
 * MIRROR path, where no flow read delivered a copy. The empty string when it can name nothing,
 * which the store refuses, so the ask takes the unnamed branch instead.
 *
 * THE SERVER'S OWN STRING WHEN IT SENT ONE. A create on a backend with #920 serves `claim_title`,
 * exactly the string the flow read would serve for the same row, so the mirror path shows the
 * same title as the delivered one: through the strip (`stripServedClaimTitle`), which is the
 * identity on a served string, and with no bound or judgement of its own.
 *
 * DERIVED ONLY WHEN IT DID NOT: a backend without #920 sends no `claim_title`, and anything that is
 * not a non-empty string once stripped is read the same way. The derivation is the one this app
 * has always made (`clampClaimTitle` of the stored title, 80 code points, which is that backend's
 * own clamp), and it is the ONLY place the client judges whether a title names anything: a derived
 * title that names nothing comes back empty, so the question is never quoted on a gate as a blank.
 */
export const guestAskClaimTitle = (servedTitle: unknown, storedTitle: string): string => {
  if (typeof servedTitle === 'string') {
    const served = stripServedClaimTitle(servedTitle);
    if (served !== '') return served;
  }
  const derived = clampClaimTitle(storedTitle);
  return claimTitleNamesAnything(derived) ? derived : '';
};

/**
 * THE STORE'S ONLY BOUND ON A TITLE, and a defensive ceiling rather than a render bound: the
 * backend's own cap on what a guest create may store, the `max_length` on
 * `PendingQuestionCreate.title` (`app/schemas/pending_question.py`). No title the server serves is
 * longer, so on a real response it never binds.
 *
 * THE STORE DOES NOT RE-BOUND A TITLE BELOW THE SERVER'S OWN BOUND. It used to cut every entry to
 * `CLAIM_TITLE_MAX_LENGTH` code points, but the server bounds the title the gate serves by what a
 * reader can SEE (backend #920), up to this cap, so an 80-code-point cut here changed the string
 * the server had chosen: 78 joiners and then "Who can help me move?" rendered as a lone "W" and an
 * ellipsis while all 99 code points published. What the ceiling still removes is the assumption
 * that the server never regresses: the gate must not become a wall of text if it ever did.
 */
export const CLAIM_TITLE_CEILING = 500;

/**
 * `title` cut to `CLAIM_TITLE_CEILING` code points, the cut marked by an ellipsis INSIDE the
 * ceiling, so a title the ceiling did cut never reads on the gate as the whole of it. Nothing else
 * is touched, and nothing here judges what the title names. Code points, not UTF-16 units, so an
 * emoji is never cut into a lone surrogate.
 */
const ceilClaimTitle = (title: string): string => {
  // A UTF-16 length within the ceiling is a code-point count within it too, which is every real
  // title, so the common case never builds the array.
  if (title.length <= CLAIM_TITLE_CEILING) return title;
  const characters = Array.from(title);
  return characters.length <= CLAIM_TITLE_CEILING
    ? title
    : characters.slice(0, CLAIM_TITLE_CEILING - 1).join('') + ELLIPSIS;
};

/**
 * Whether `filedUnder` is an address at all: a string with something left once it is trimmed. A
 * blank or non-string value is not a missing address but a malformed one, and it refuses the entry
 * rather than being read as absent, because absent is the first-recipient fallback: a mis-filed
 * entry must not be promoted into the rule that lets it travel further.
 */
const isFiledUnderAnAddress = (filedUnder: unknown): filedUnder is string =>
  typeof filedUnder === 'string' && normalizeSendAddress(filedUnder).length > 0;

const isClaimEntry = (value: unknown): value is PendingQuestionClaimEntry => {
  if (typeof value !== 'object' || value === null) return false;
  const { secret, title, askedAt, filedUnder } = value as Record<string, unknown>;
  return (
    (filedUnder === undefined || isFiledUnderAnAddress(filedUnder)) &&
    typeof secret === 'string' &&
    secret.length > 0 &&
    // SHAPE, NOT JUDGEMENT: a title is a non-empty string, and nothing here asks what it names.
    // The server answers that for every title it serves (backend #920: the create refuses a title
    // the gate could not show, and the flow read leaves out a row whose title shows nothing), and
    // the guest submission answers it for the one title the client derives itself
    // (`guestAskClaimTitle`). A second judgement here, by a definition of "names anything" that is
    // not the server's, is how the store came to drop questions the server had named.
    typeof title === 'string' &&
    title.length > 0 &&
    typeof askedAt === 'string' &&
    !Number.isNaN(Date.parse(askedAt))
  );
};

/**
 * THE ONE PLACE THE LIST IS BOUNDED, and every producer goes through it: the store's
 * writer, the store's reader, and the flow-context validator. So no path -- the door's
 * upload, the claim's body, the gate's render -- can be handed more than the wire accepts.
 *
 * THE STORE BOUND IS THE WIRE BOUND, and the equality is the design rather than a coincidence:
 * a store that could hold more than one request publishes let the gate NAME six questions while
 * Continue posted five, on the one screen whose entire job is to say what Continue does. The
 * alternative -- keep everything, send what fits -- needs a remainder with a consent story of its
 * own ("these five now, the rest next time") and there is nowhere to tell it; the retire wiped the
 * unsent entry on settle anyway, so the remainder was never carried and the promise was the only
 * thing the extra entry ever bought.
 *
 * NEWEST FIRST, and the cap drops the OLDEST. Two reasons, and they agree: the question a
 * visitor just asked is the one they are signing in to publish, and an older row is the one
 * more likely to have been claimed or expired already (the backend's flow read simply
 * omits those, so spending a slot on one buys nothing). Newest-first is also the order the
 * gate should read in. The sort is stable, so entries sharing an `askedAt` keep the order
 * they arrived in.
 *
 * WHAT THE DROP COSTS, stated rather than glossed: a sixth ask from ONE TAB before a single
 * sign-in loses the oldest entry's NAME. The row itself is untouched server-side and lives to its
 * TTL. It is not logged -- `warn` is stripped from a production build, so it could not reach the
 * field anyway, and `error` would fire on behaviour this function performs by design -- so the
 * eviction is pinned by test instead (`caps the list at the wire bound`).
 *
 * Deduped by secret, first occurrence winning, because the same row named twice is not two
 * questions and would spend a slot of the budget resolving the same row.
 */
export const normalizeClaimEntries = (entries: readonly unknown[]): PendingQuestionClaimEntry[] => {
  const bySecret = new Map<string, PendingQuestionClaimEntry>();
  for (const entry of entries) {
    // PER-ENTRY, so one bad entry costs one question rather than all of them. A whole-list
    // rejection here would let a single malformed record -- a shape from another bundle, a
    // truncated write -- silently discard four good claims and hand the arrival to the
    // flag-only path.
    if (!isClaimEntry(entry)) continue;
    if (!bySecret.has(entry.secret)) {
      // CEILINGED HERE, on every read and every write, so every render and every wire path is
      // bounded whatever wrote the store -- and by the ceiling alone, never below the server's
      // own bound (`CLAIM_TITLE_CEILING`). The ceiling is the title's; the address is not cut.
      //
      // THE ADDRESS IN ONE FORM, and here for the same reason: every writer and every reader goes
      // through this function, so the tab and the mirror both hold the form the door compares a
      // send against (trimmed and lower-cased, `normalizeSendAddress`), whoever wrote the entry.
      // Native Preferences hold no address (`storeNativePendingQuestionClaim`). An entry filed
      // under no address gains no key.
      bySecret.set(entry.secret, {
        ...entry,
        title: ceilClaimTitle(entry.title),
        ...(entry.filedUnder === undefined
          ? {}
          : { filedUnder: normalizeSendAddress(entry.filedUnder) }),
      });
    }
  }
  // SORTED BEFORE SLICED, so the cap drops the oldest rather than whatever arrived last, and
  // AFTER the dedupe, so a repeated secret cannot evict a distinct question.
  //
  // KNOWN BOUND: the drop is by ask time, not by arrival, so a NEW entry whose `askedAt` sorts
  // oldest of the six is the one the cap drops -- and `storeGuestPendingQuestionClaim` still
  // reports the write as landed, correctly, because the store ends up in exactly the state this
  // function intends. It takes a clock anomaly to reach (a create response whose time predates
  // five already-stored asks), and dropping the oldest ask is the rule either way, so it is
  // recorded here rather than special-cased in the writer.
  return [...bySecret.values()]
    .sort((a, b) => Date.parse(b.askedAt) - Date.parse(a.askedAt))
    .slice(0, PENDING_QUESTION_CLAIMS_MAX);
};

/**
 * The secrets ONE REQUEST may carry, in the order they were handed over.
 *
 * A SECOND SLICE OVER AN ALREADY-BOUNDED LIST: every caller today hands it a
 * `normalizeClaimEntries` result -- the tab read, the native read and anything derived from
 * either -- so on the code as it stands this is the identity function. It stays
 * because this is the WIRE boundary and a request over the cap does not fail loudly -- 6-32
 * entries make the backend drop the claims half while keeping the intent and the destination.
 * What it guarantees is the LENGTH and nothing more: "the newest" is a property of
 * `normalizeClaimEntries`'s sort, not of this slice, so on an unsorted list it would keep the
 * first five rather than the newest five. Sorting is not repeated here because doing it twice
 * would read as though one of the two were load-bearing.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: an earlier version of this comment argued the cap was a wire
 * bound AND NOTHING ELSE -- storage keeping every entry, "a row beyond the cap WAITS for the next
 * claim rather than losing its name". It did not wait: `clearClaimedPendingQuestionSecrets` retires
 * the whole entries key on settle, so the unsent row lost its name anyway, and in the meantime an
 * over-cap store let the gate name more questions than Continue could publish. The store bound and
 * the wire bound are therefore one number, applied in `normalizeClaimEntries`. Restoring an
 * unbounded store reopens the gate/publish mismatch, so it needs the remainder consent story
 * first.
 */
export const claimSecretsForRequest = (entries: readonly PendingQuestionClaimEntry[]): string[] =>
  entries.slice(0, PENDING_QUESTION_CLAIMS_MAX).map(entry => entry.secret);

/**
 * Whether a parsed value is a list WORTH NORMALISING: an array with at least one usable entry
 * in it.
 *
 * The validator the mirror reader is given: a mirror is JSON this app wrote, but it is
 * also 30 minutes old and readable by any tab in the profile, so it is re-checked rather
 * than trusted. A rejected envelope is REMOVED by `readMagicLinkMirror`, which is what a
 * mirror written by the previous bundle (a bare `true`) gets.
 *
 * WHAT THE DROP COSTS, stated rather than glossed: the tab REDEEMING the link then holds no
 * entries and no flag, so `hasPendingQuestionClaim` answers false and the settle ladder returns
 * without posting anything -- the device-token limb is not a fallback there, because there is no
 * claim in that tab to fall back for. That limb survives only where the flag is armed, which is
 * the tab that ASKED, and on the magic-link path that tab spends it only through a code or social
 * sign-in finished there before any redemption starts: with the continuation off it sits on the
 * door's sent confirmation (`AUTH_DOOR_COPY.confirmation`) and never runs `/callback`, and with it
 * on an approval made elsewhere has `redeem()` defer the flag. So a bare-boolean mirror written by
 * an old bundle costs the question, and for as long as such a tab stays open it keeps writing one.
 * Whether that scope should instead be offered on a gate is the same open question as the
 * flag-only claim below.
 *
 * IT NARROWS TO `unknown[]` AND NOTHING FURTHER, which is the honest type for what it checked:
 * it asks only "is this a list with SOMETHING usable in it", so the elements are unjudged and a
 * narrowing to `PendingQuestionClaimEntry[]` let a caller read `.secret` off a `null` with the
 * compiler's blessing. Every all-or-nothing answer at this boundary throws away good claims over
 * one bad one -- a sixth question would have cost the visitor the other five, and one record
 * written by another bundle would have cost them all of them -- so the per-entry judgement stays
 * in `normalizeClaimEntries`, and the type says so.
 */
export const isPendingQuestionClaimEntries = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.some(entry => isClaimEntry(entry));

/**
 * Whether this tab is holding a DEFERRED claim: an unnamed claim owed here that no gate has
 * consented to, which only a gate may spend. Three writers leave one: a consented secrets claim
 * that could not present it, `/auth/link`'s narrowing when no entry is left beside the flag, and
 * the door before its own redemption of an approval made elsewhere, and the door's stand-downs and
 * `AuthProvider`'s cross-tab sign-in when another tab of this browser redeems instead. Read by
 * `/auth/link` and by nothing else -- deliberately not by `hasPendingQuestionClaim`, which the
 * ungated ordinary path calls.
 */
export const readDeferredPendingQuestionClaimInTab = (): boolean => {
  try {
    return (
      sessionStorage.getItem(PENDING_QUESTION_DEFERRED_KEY) === PENDING_QUESTION_DEFERRED_VALUE
    );
  } catch (err) {
    logger.warn('[pendingQuestion] sessionStorage unavailable for deferred claim read', err);
    return false;
  }
};

/** Whether this tab's claim arrived on the wire rather than from this browser profile. */
const wasClaimDeliveredToTab = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_QUESTION_SOURCE_KEY) === PENDING_QUESTION_SOURCE_DELIVERY;
  } catch {
    // Unreadable storage cannot prove a claim is this profile's own, and the mirror is
    // the broadcast side: answer the way that withholds rather than the way that shares.
    return true;
  }
};

/**
 * The claim entries a send to `recipient` may carry: every entry filed under that address, and an
 * entry filed under no address only on a send the door reads back as its own first recipient
 * (`capabilityRuleFor`'s `bind`).
 *
 * The claim's member of the first-recipient family (`stripIntentCapabilities` for the intent,
 * `stripCapabilityParams` for the destination), and the only one keyed on the capability's OWNER
 * rather than on send ORDER. An entry is a per-row SECRET plus the title that secret resolves to,
 * with no "plain half" to let through the way a `JOIN_COMMUNITY` survives losing its invite token,
 * so an entry travels whole or not at all.
 *
 * WHY A SECRET IS A CAPABILITY and not just a matcher: the flow read resolves one to its
 * question's title and ask time with no address check (`resolve_claims` filters on the secret
 * hash, `is_claimed` and `expires_at`), so a secret in another person's link names this person's
 * unpublished question on THEIR gate, delivered to their mailbox. TECH-540 is the ruling;
 * `docs/magic-link/current-design/decisions.md` › R-FILED carries the rule it applies.
 *
 * WHY THE OWNER AND NOT THE ORDER (review 5330705557, R07). The order is written only when this tab
 * SENDS, so a guest who asked, reached the door and left without sending bound nothing, and the
 * next person to send from the tab was the "first recipient": the first guest's secret went out in
 * the second person's link. An entry's own address does not depend on who sent first, and keying
 * on it costs nothing publishable: the claim's secret limb also requires the row's address to be
 * the signing-in account's, so an entry carried to any other address could never have published
 * through that link.
 *
 * AN ENTRY FILED UNDER NO ADDRESS (stored before `filedUnder` existed) keeps the order rule, the
 * TECH-540 binding, for the one flow TTL such an entry lives: it travels only on a send whose
 * `isFirstRecipient` is true. The door takes that answer from its binding READ BACK after the
 * send binds (`capabilityRuleFor` in `AuthDoor.tsx`), so in a tab whose storage refuses the
 * binding it is false on every send, and such an entry travels to nobody, the first address
 * included.
 *
 * `recipient` is trimmed and lower-cased here, and the entry's side already is
 * (`normalizeClaimEntries`): the backend files the row under the lower-cased address and its secret
 * limb compares lower-cased on both sides, and the door trims what it sends.
 */
export const claimEntriesForRecipient = (
  entries: readonly PendingQuestionClaimEntry[],
  recipient: string,
  { isFirstRecipient }: { isFirstRecipient: boolean },
): PendingQuestionClaimEntry[] => {
  const address = normalizeSendAddress(recipient);
  return entries.filter(entry =>
    entry.filedUnder === undefined ? isFirstRecipient : entry.filedUnder === address,
  );
};

/**
 * Copies the per-tab claim ENTRIES into localStorage so they survive the magic link
 * opening in a different tab, and RETURNS what this tab may carry TO THIS RECIPIENT -- which
 * the door needs anyway, one line later, for the flow context it uploads.
 *
 * An EMPTY list CLEARS the mirror, same as the other two mirrors: entering the door is
 * where the mirror is made to describe THIS flow, and a survivor from an abandoned one
 * would make an unrelated later sign-in go looking for a claim that is not theirs. A
 * DELIVERED claim clears it too, for the reason on `PENDING_QUESTION_SOURCE_KEY`: it
 * still travels on the wire, where it is addressed, but it does not join the
 * profile-wide envelope.
 *
 * A flag-only claim (entries empty, flag armed -- a row whose create predates minted secrets)
 * mirrors NOTHING, where the boolean mirror carried it. The reason is the design: a claim carried
 * cross-tab with no question to name is precisely the content-blind Continue this removes.
 *
 * `sanitize` is the CAPABILITY BINDING, the same seam the intent and the destination mirrors
 * take: the door passes `claimEntriesForRecipient` for the address it is sending to, on EVERY
 * send, because an entry travels by the address it was filed under and not by send order. It is
 * applied here rather than at the call site because this one call is BOTH halves -- the envelope
 * written below and the list the door uploads, which is this function's return value -- so a
 * bound send has no unbound copy of the entries anywhere.
 *
 * Absent only at the door's MOUNT, which has no address yet, so that write puts every held entry
 * on the envelope whoever it is filed under; the send that follows rewrites it filtered. A mount
 * with no send leaves the unfiltered envelope for its TTL, and its only reader is an `/auth/link`
 * hydration in this same browser profile, never a mailbox (review 5330705557, R07). That landing
 * names and commits only the entries filed under its own link's address (`localQuestionClaims`
 * there; Dexter, 2026-09-29), so another address's entry reaches that tab and goes no further.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: what that costs depends on the continuation flag
 * (`featureFlags.magicLinkContinuation`, set from `VITE_MAGIC_LINK_CONTINUATION`).
 *
 * WITH THE FLAG OFF it is a row nothing claims, and the "still claims by device token in the
 * context that asked" answer does not reach the ORDINARY flow. The emailed link opens in a NEW
 * tab (`magicLinkMirror`), which hydrates nothing here and so never posts; the tab that asked
 * sits on the door's sent confirmation (`AUTH_DOOR_COPY.confirmation`) and never runs
 * `/callback`. So on the normal magic-link path such a row is not claimed by any limb and
 * expires. The drop is KEPT whatever the flag, and in this flag state the question is lost:
 * TECH-509 was this question, in three costumes, and Dexter accepted the loss when he cancelled
 * it, with TECH-534, on 2026-09-23. Mirroring the flag to feed the unnamed-scope gate in the
 * redeeming tab was the alternative, so restoring it would reopen that ruling rather than fix an
 * oversight.
 *
 * WITH THE FLAG ON the same holds wherever the link's own tab signs in: a new tab of this
 * browser, or the device that opened the link signing in there. An APPROVAL on another device is
 * different: the tab that asked redeems the approved token itself (`useClaimContinuation` in
 * `AuthDoor.tsx`) and runs `/callback`, where a flag-only claim would be spent by device token
 * with no gate naming it, on both of its paths: the settle (`settlePendingQuestionClaim`) for an
 * existing user, and SignupFlow's `claimPendingIfAny` for a new user, whom `/callback` sends to
 * `/signup` first. Dexter decided webapp #1187 on 2026-09-26, and `redeem()` now turns an armed
 * unnamed flag into the deferred marker before it signs in, on the web
 * (`deferUnnamedPendingQuestionClaimInTab`), so only a gate can spend it; so does every stand-down
 * for a flow another tab of this browser redeemed instead, and every cross-tab SIGNED_IN delivered
 * to the tab while it runs (`handleSignedInInOtherTab` names the sign-ins that are not). The
 * continuation never visits `/auth/link`, so on that path too the question is in practice lost,
 * the same accepted loss.
 */
export const mirrorPendingQuestionClaimForMagicLink = (
  sanitize?: (entries: readonly PendingQuestionClaimEntry[]) => PendingQuestionClaimEntry[],
): PendingQuestionClaimEntry[] => {
  const held = readPendingQuestionClaimEntriesInTab();
  const entries = sanitize ? sanitize(held) : held;
  // The tab's own copy is untouched by the strip, exactly as it is for the other two: what
  // the strip costs is the cross-context CARRY. The entries left here claim by secret, never
  // by device token, so they publish only if the visitor signs in from this tab under the
  // address the row was filed under; otherwise the question is lost, the loss accepted with
  // TECH-509.
  const isProfileOwned = entries.length > 0 && !wasClaimDeliveredToTab();
  writeMagicLinkPayloadMirror(
    MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim,
    isProfileOwned ? entries : null,
  );
  return entries;
};

/**
 * Copies the cross-tab mirror into THIS tab's sessionStorage, and drops the
 * mirror. Called once, by `/auth/link`, at the moment a magic-link redemption is
 * actually starting.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this replaces a fallback that used to sit
 * inside `hasPendingQuestionClaim`, i.e. it was consulted on EVERY callback in
 * the app rather than on magic-link ones. The claim was matched by the backend on
 * device token OR email, so a guest's abandoned pending question, mirrored on a
 * door entry and never swept (the sweep runs on sign-out, which never happened),
 * was claimed onto the account of the next person to sign in on that browser
 * profile inside the TTL -- a family laptop, a library machine, a hot desk, i.e.
 * exactly the population this feature is easiest for. Hydrating at `/auth/link`
 * binds the entries to the flow that wrote them; the bare email limb is gone from
 * the backend, so a hydrated entry is also the only way a cross-context claim can
 * name anything at all.
 *
 * Returns whether anything was hydrated.
 */
export const hydratePendingQuestionClaimFromMagicLinkMirror = (): boolean => {
  const mirrored = readMagicLinkMirror(
    MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim,
    isPendingQuestionClaimEntries,
  );
  writeMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim, null);

  if (!mirrored) {
    return false;
  }

  // NORMALISED HERE, because the validator above narrows to `unknown[]` and means it: it
  // established that the list holds at least one usable entry, not that every element is one.
  // The writer normalises again, so this is idempotent -- it is the TYPE that has to be earned.
  const armed = setPendingQuestionClaimInTab(normalizeClaimEntries(mirrored));
  if (!armed) {
    // RECORDED HERE, where the arm's other callers record it at their own call sites,
    // because this one's caller cannot:
    // `/auth/link`'s mount effect invokes this as a bare statement (the three hydrators
    // run for their effect), so a `false` returned into nothing was silent in every
    // build -- and the mirror is dropped either way, so a claim this profile was
    // carrying is simply gone with nothing said about it anywhere. `error` is the one
    // level a production build keeps, and it is the level the commit site uses for the
    // same loss. No values in the line.
    logger.error('[pendingQuestion] the mirrored question claim did not reach this tab');
  }
  return armed;
};

/**
 * The mirror hydrator's arm (`hydratePendingQuestionClaimFromMagicLinkMirror`): this tab's
 * claim becomes the entries the mirror carried. The write and its contract are
 * `armClaimInTab`'s.
 */
export const setPendingQuestionClaimInTab = (
  entries: readonly PendingQuestionClaimEntry[],
): boolean => armClaimInTab(entries);

/**
 * PROMOTES a deferred claim back into a real one, because a gate named its scope and the visitor
 * clicked through. The one writer of the unnamed flag outside the guest submission.
 *
 * Arms the flag and keeps whatever named entries the tab holds: consent to the unnamed scope is
 * not a reason to discard a named question sitting beside it.
 */
export const promoteDeferredPendingQuestionClaimInTab = (): boolean => {
  // The DEFERRED key goes and the flag arrives; the DELIVERY bit is left exactly as it was, and
  // that now falls out of the arm's own rule rather than needing an opt-out: this call hands the
  // tab's entries straight back, so every entry is INHERITED and the bit travels with them. An
  // earlier version stripped the provenance from delivered entries here, and a door revisit then
  // mirrored them profile-wide.
  try {
    sessionStorage.removeItem(PENDING_QUESTION_DEFERRED_KEY);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to consume the deferred claim:', err);
  }
  return armClaimInTab(readPendingQuestionClaimEntriesInTab(), { unnamed: true });
};

/**
 * Arms a claim THE LINK DELIVERED, which is the same write plus the provenance bit that
 * keeps it out of the profile-wide mirror. See `PENDING_QUESTION_SOURCE_KEY`.
 *
 * Two callers: `/auth/link`'s Continue, which REPLACES the tab's entries with what its gate
 * named, and the door's cold resume (`AuthDoor.tsx`, #1261), which has no gate and so passes the
 * delivery MERGED with the tab's own entries for the link's address. On native with an empty tab
 * the door does not call this at all, so the claim's send reads the durable list as it would
 * without the door.
 */
export const setDeliveredPendingQuestionClaimInTab = (
  entries: readonly PendingQuestionClaimEntry[],
): boolean => armClaimInTab(entries, { delivered: true });

/**
 * THE GUEST SUBMISSION'S ONE WRITE, covering every store a claim lives in on the
 * platform it is running on.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the create site used to make three separate storage
 * calls, one of them a raw `sessionStorage.setItem` with no enclosing `try` -- and the
 * access ITSELF throws where Web Storage is refused (iOS Safari with "Block All
 * Cookies", hardened mail WebViews, this flow's own population), so the throw escaped
 * the handler's async IIFE as an unhandled rejection AFTER the row had been stored
 * server-side: no toast, no `signIn()`, and a visitor left on the form with a pending
 * question created for them and nothing said about it.
 *
 * `entry` is NULL when the create response carried no `claim_secret` -- a backend that
 * predates minted secrets, or any row whose secret is absent. The flag is still armed,
 * because that guest's claim still works through the device-token limb in the browsing
 * context that wrote the row; what it cannot do is travel, and nothing here pretends
 * otherwise.
 *
 * IT APPENDS rather than replaces, which is the one place this store does. A guest can ask
 * twice before signing in (abandon the handoff, come back, ask again), and a replace would
 * leave the first question claimable only by device token -- i.e. lost to the
 * cross-context path the carrier exists for. `normalizeClaimEntries` bounds the result, so
 * the sixth ask drops the oldest instead of costing the request its whole claims half.
 *
 * AND IT IS THEREFORE THE ONE WRITER THAT CAN PRODUCE A MIXED LIST: a tab that committed a
 * DELIVERED claim and then asks a question of its own holds both kinds at once. The arm's
 * provenance rule is what keeps that list off the profile-wide mirror -- see
 * `inheritsDeliveredEntries` in `armClaimInTab`.
 *
 * Returns whether THIS TAB's write landed. The native write is fire-and-forget by
 * construction (Preferences is async and the caller is one statement from a full-page
 * handoff), and records its own failure.
 */
export const storeGuestPendingQuestionClaim = (
  entry: PendingQuestionClaimEntry | null,
): boolean => {
  // `: read()` and NOT `: []`. A create whose response carried no secret must not DESTROY the
  // secret an earlier ask stored -- that left the tab flag-armed with no entries, so two rows
  // published unnamed where one of them was nameable. The docblock above already claimed this
  // appends; it did not.
  const existing = readPendingQuestionClaimEntriesInTab();
  // An entry-less create means an UNNAMED row now exists, which is its own fact and is recorded
  // as one -- alongside, never instead of, the named entries already stored.
  const armed = entry
    ? armClaimInTab([entry, ...existing])
    : armClaimInTab(existing, { unnamed: true });
  if (isNativePlatform()) {
    // MERGED with the durable list rather than written over it, because on native the tab is the
    // volatile store and Preferences is the one that survives. `existing` above reads
    // sessionStorage, which an app kill mid-auth has already emptied -- so a second ask after a
    // kill arrived here with a one-entry list and replaced a durable list that still held the
    // first ask's secret, the exact loss Preferences is kept for. `normalizeClaimEntries` inside
    // the merge dedupes and applies the same cap, so the newest asks survive and the oldest is the
    // one dropped.
    //
    // THE LIST THE ARM WAS HANDED, not a read-back of the tab (review 5330705557, R11). An arm that
    // fails closed has already discarded the tab (`discardClaimInTab`), so a read-back fed the
    // merge the old list and the new ask never reached Preferences, although the Preferences write
    // did not fail. The durable store is the one that survives; it must not be fed from the
    // volatile store that just failed. On a landed arm the two inputs normalise to the same list.
    void mergeNativePendingQuestionClaim(entry ? [entry, ...existing] : existing);
  }
  return armed;
};

/**
 * Writes the native durable claim as the UNION of what this tab holds and what already survived
 * there, which is what makes `storeGuestPendingQuestionClaim`'s "IT APPENDS" true on native too.
 *
 * Module-private and native-only: the web store has no equivalent, because unioning the tab with
 * the durable copy on the WEB side would let a previous flow's entries ride along with a claim a
 * gate has just consented to (see `readPendingQuestionClaimSecrets`). Native shows no gate, so
 * there is no consented set for a durable entry to contradict -- only rows this device asked for.
 */
const mergeNativePendingQuestionClaim = async (
  tabEntries: readonly PendingQuestionClaimEntry[],
): Promise<void> => {
  const durable = await readNativePendingQuestionClaimEntries();
  await storeNativePendingQuestionClaim(normalizeClaimEntries([...tabEntries, ...durable]));
};

/**
 * THE ONE WRITE of the per-tab claim: the questions it may publish, the unnamed flag when asked
 * for, and the provenance bit. Returns whether the write landed.
 *
 * Seven callers, one write: the guest submission that creates the pending row
 * (`storeGuestPendingQuestionClaim`), the mirror hydration (through
 * `setPendingQuestionClaimInTab`), `/auth/link`'s Continue for a claim the LINK itself carried
 * (`flow_context.pending_question_claims`, through `setDeliveredPendingQuestionClaimInTab`), the
 * door's cold resume committing the same carried claim with no gate (`AuthDoor.tsx`, #1261, through
 * `setDeliveredPendingQuestionClaimInTab`), a gate's promotion of a deferred claim
 * (`promoteDeferredPendingQuestionClaimInTab`), `/auth/link`'s commit keeping the claim to the
 * entries filed under its link's address (`keepPendingQuestionClaimEntriesFiledUnder`), and
 * `/auth/link`'s mount dropping the entries filed under no address when its binding did not bind
 * (`keepFiledPendingQuestionClaimEntriesInTab`). A guard added in one of them reaches one caller of
 * seven; a rule about the claim's write goes here.
 *
 * IT REPLACES THE ENTRIES RATHER THAN ADDING TO THEM, which is what makes "Continue
 * commits exactly the secrets the gate showed" true: the gate shows the delivery when
 * there is one and the tab's own entries otherwise, never a union of the two. The two
 * callers that must not replace pass the union in themselves: the guest submission, which
 * appends, and the door's cold resume, which has no gate to justify a replace and so merges
 * the delivery with the tab's own entries for the link's address.
 *
 * IT DOES NOT TOUCH THE MIRROR, and that is the boundary rather than an omission.
 * The mirror is armed at the DOOR from what a tab already holds, and hydrating it
 * drops it; re-arming it here would take a claim the server just delivered for one
 * redemption and offer it to the next link opened in this browser profile, which is
 * the bleed the hydrate-then-drop design exists to close.
 *
 * Native Preferences are not written either. `/auth/link` on native is a dead end that hands
 * off to a browser, so its callers never run there. The door's cold resume DOES run on native
 * (the app reopens on an approved flow it sent, `AuthDoor.tsx`, #1261), and when an app kill has
 * emptied the tab it makes no claim write at all, though it still writes the carried intent and
 * destination. The claim's reader reads the tab first and the durable list only when the tab is
 * empty (`readPendingQuestionClaimSecrets`), so a claim write into that empty tab would hide the
 * durable list from the sign-in, and merging the list in would give its address-less asks send
 * order, so a resend would carry one made under another address. With no entries written, the
 * claim's send presents the durable list exactly as it would without the door. A delivered copy
 * is not written to Preferences either: it would outlive its redemption in a store with no TTL
 * (TECH-485). The create site, which IS reachable on native, writes Preferences itself:
 * `storeGuestPendingQuestionClaim` merges into the durable list after this write.
 */
const armClaimInTab = (
  candidates: readonly PendingQuestionClaimEntry[],
  { delivered = false, unnamed = false }: { delivered?: boolean; unnamed?: boolean } = {},
): boolean => {
  // Bounded and deduped BEFORE the write, so nothing downstream -- the door's upload, the
  // claim's body, the gate's render -- can be handed more than the wire accepts. See
  // `normalizeClaimEntries` for what the cap drops and why.
  const entries = normalizeClaimEntries(candidates);
  // ONE WRITE MUST NOT SPEAK FOR A MIXED LIST'S PROVENANCE, and this is the fact that stops it.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: the bit is per-KEY and the entries are a LIST, so the only
  // statement it can safely make about a list holding both kinds is the withholding one. The
  // guest submission APPENDS (see `storeGuestPendingQuestionClaim`), so a visitor who asks their
  // own question in a tab that already committed a DELIVERED claim produces exactly that mixed
  // list -- and a caller declaring the whole list its own then stripped the bit, after which the
  // door mirrored the delivered secret and its title profile-wide and the next `/auth/link` in
  // this profile named someone else's question on its gate. Keyed on INHERITANCE and not on the
  // new entry, because an entry-less ask (`unnamed`) hands the delivered entries straight back
  // and stripped the bit identically.
  const inheritsDeliveredEntries =
    wasClaimDeliveredToTab() &&
    readPendingQuestionClaimEntriesInTab().some(held =>
      entries.some(entry => entry.secret === held.secret),
    );
  try {
    // THE FLAG MEANS ONE THING: "this browser may hold a row nothing can NAME." Only a write that
    // passes `unnamed` arms it: a create with no secret (`storeGuestPendingQuestionClaim`) and the
    // promotion of a deferred claim (`promoteDeferredPendingQuestionClaimInTab`). A write of named
    // entries that LANDS neither sets nor clears it; ANY write that fails drops it with the
    // entries, because both fail-closed exits below run `discardClaimInTab`.
    //
    // AIDEV-NOTE [MEETBALL magic-link]: it used to be armed UNCONDITIONALLY, beside the entries,
    // and that made the unnamed path steady state for every visitor rather than a transition
    // artefact: every post-secrets create left a flag a later claim could spend by device token,
    // publishing rows nothing had named. Keying it on `entries.length === 0` instead was the
    // first correction and was also wrong, in the other direction: a second ask whose response
    // carried no secret, made while a named entry was already stored, then recorded its unnamed
    // row NOWHERE. So the two facts are independent and are stored independently. "Is a claim
    // pending" is `hasPendingQuestionClaimInTab` (flag OR entries).
    if (unnamed) {
      sessionStorage.setItem(PENDING_QUESTION_KEY, PENDING_QUESTION_VALUE);
    }
    // Written or REMOVED, never left: a claim with nothing to name must not inherit the
    // entries of the claim before it, which is how a refused delivery followed by an
    // honest same-tab ask would publish the refused question.
    if (entries.length > 0) {
      sessionStorage.setItem(PENDING_QUESTION_CLAIMS_KEY, JSON.stringify(entries));
    } else {
      sessionStorage.removeItem(PENDING_QUESTION_CLAIMS_KEY);
    }
    // Written or REMOVED, never left, and the REMOVAL is the one that needs a condition. This tab
    // can arm a claim of its own after a delivered one was refused, and a stale bit would keep
    // that honest claim off the mirror it belongs in -- the refusal clears the entries, so nothing
    // is inherited and the bit goes. What must not happen is a write that KEEPS delivered entries
    // and calls the result its own.
    if (delivered || inheritsDeliveredEntries) {
      sessionStorage.setItem(PENDING_QUESTION_SOURCE_KEY, PENDING_QUESTION_SOURCE_DELIVERY);
    } else {
      sessionStorage.removeItem(PENDING_QUESTION_SOURCE_KEY);
    }
  } catch {
    // FAIL CLOSED, for the same reason the read-back below does: the caller may be REPLACING
    // mirror-hydrated entries with the set a gate just showed, and a throw part-way leaves the
    // unconsented set in place while the caller logs and carries on to `signIn`. Dropping this
    // tab's claim is the safe direction -- an unpublished question survives to its TTL, an
    // unconsented publish does not come back.
    discardClaimInTab();
    // No log: the caller is the one that can say what the loss MEANS, and all SEVEN of
    // them record it at `error` (the only level a production build keeps) -- the hydrator
    // above, `/auth/link` at its delivered commit, at its narrowing to the link's address, at
    // its promotion and at its mount's drop of the unfiled entries, the door at its cold
    // resume's carried commit (`AuthDoor.tsx`), and the guest submission in
    // `useCreateQuestionSubmit`. A DEV-only warn
    // here on top of that was one failure told twice, and the quieter half was the one nobody
    // in the field could see.
    return false;
  }
  // A READ-BACK, not a bare `try`/`catch`, and the same shape as
  // `write` in `@utils/sessionSlot`, which arms the signed-in cue (it reads its
  // entry back for exactly this reason). Web Storage can ACCEPT a write and keep
  // nothing -- a partitioned WebView, a storage bucket evicted under pressure --
  // and accepted-and-discarded is the same failure to the visitor as refused, while
  // only one of the two throws. The caller is the one that can say something about
  // it: `/auth/link` logs the false case at the commit, because a claim the visitor
  // consented to and the tab then lost is a question that never gets posted.
  //
  // BOTH HALVES are read back, because the entries are what make the claim publishable
  // now: a flag that survived without them is a claim that can name nothing, which on
  // the cross-context path is a claim that matches nothing at all. The delivery bit is
  // not read back; `PENDING_QUESTION_SOURCE_KEY` says why that holds.
  //
  // A REFUSED write is silent here on purpose, like the cue writer: the return value is
  // the report, and the caller owns what it means. A refused READ-BACK is not silent --
  // `readUnnamedPendingQuestionClaimInTab` warns, DEV-only, on the mixed case a partitioned
  // WebView can produce (write accepted, read refused) -- and that warn belongs to the
  // reader, which is used on paths that have no caller to report to.
  // COMPARED BY SECRET, not by count, and a mismatch FAILS CLOSED.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: a count comparison passed for a same-size STALE set,
  // which is the case that matters: `/auth/link` calls this to REPLACE mirror-hydrated entries
  // with the ones the gate showed, so a refused or discarded entries write left the unconsented
  // set in sessionStorage, and the caller logged and went on to `signIn` -- publishing exactly
  // what the gate did not name. So the entries are read back and compared, and anything short of
  // the intended set clears the claim outright: losing a consented publish is recoverable (the
  // row survives to its TTL), publishing an unconsented one is not.
  const written = readPendingQuestionClaimEntriesInTab();
  // ...and `written` is the answer to the flag-or-entries question too, so it is not parsed a
  // second time: sessionStorage is synchronous and per-tab, so a second read one statement later
  // cannot differ from this one.
  const landed =
    (unnamed ? readUnnamedPendingQuestionClaimInTab() : claimIsPending(written)) &&
    written.length === entries.length &&
    written.every(entry => entries.some(intended => intended.secret === entry.secret));
  if (!landed) {
    discardClaimInTab();
  }
  return landed;
};

/**
 * Drops THIS WRITE'S subject and nothing else: the flag, the named entries and the provenance bit.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the fail-closed paths used to call
 * `clearPendingQuestionClaimInTab`, which also removes the DEFERRED marker and nulls the
 * profile-wide MIRROR -- two facts a failed arm has no standing to destroy. A storage fault on
 * the entries write then took a legacy row's only claim path (the marker) and another tab's
 * carried claim with it, and it contradicted the arm's own "IT DOES NOT TOUCH THE MIRROR" (on
 * `armClaimInTab`). A REFUSAL still clears everything -- see `clearPendingQuestionClaimInTab` --
 * because a refusal is a statement about the claim and a storage fault is not.
 */
const discardClaimInTab = (): void => {
  try {
    // Written out rather than spread from `PENDING_QUESTION_TAB_KEYS`: the omission of the
    // deferred key is the judgement this function exists to make.
    sessionStorage.removeItem(PENDING_QUESTION_KEY);
    sessionStorage.removeItem(PENDING_QUESTION_CLAIMS_KEY);
    sessionStorage.removeItem(PENDING_QUESTION_SOURCE_KEY);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to discard the claim:', err);
  }
};

/**
 * THE UNION, over an entries list the caller has already read: an unnamed claim, a named one, or
 * both. One definition, two ways in -- `hasPendingQuestionClaimInTab` reads the list itself, and
 * the arm's read-back passes the list it read a statement earlier rather than parsing it twice.
 */
const claimIsPending = (entriesInTab: readonly PendingQuestionClaimEntry[]): boolean =>
  readUnnamedPendingQuestionClaimInTab() || entriesInTab.length > 0;

/**
 * Whether this tab is carrying a claim AT ALL: a named one, an unnamed one, or both.
 *
 * THE PREDICATE THE CLAIM LADDER ASKS, and it is deliberately broader than the flag. Since the
 * flag is armed only for a row nothing can name, "is a claim pending" has to be the union -- a
 * post-secrets guest has entries and no flag, and their claim must still run. It is also the
 * first leg of the async `hasPendingQuestionClaim` in `useClaimPendingQuestions` (which adds
 * native Preferences), and it is what `useSignInOut` asks on every sign-in. Deliberately NOT a
 * reader of the deferred marker: see `PENDING_QUESTION_DEFERRED_KEY`.
 */
export const hasPendingQuestionClaimInTab = (): boolean =>
  claimIsPending(readPendingQuestionClaimEntriesInTab());

/**
 * Whether this tab is carrying an UNNAMED claim: a row nothing can name, publishable only by the
 * device-token limb. `hasPendingQuestionClaimInTab` is the question about claims in general.
 */
export const readUnnamedPendingQuestionClaimInTab = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_QUESTION_KEY) === PENDING_QUESTION_VALUE;
  } catch (err) {
    logger.warn('[pendingQuestion] sessionStorage unavailable for claim read', err);
    return false;
  }
};

/**
 * The questions THIS TAB's claim would publish, read synchronously. Empty when the tab
 * holds no claim, when it holds a flag with no named questions, or when storage is
 * refused.
 *
 * Re-validated on the way out rather than trusted: the value survives a deploy, so a
 * shape written by another bundle reads as nothing rather than as a half-parsed entry.
 */
export const readPendingQuestionClaimEntriesInTab = (): PendingQuestionClaimEntry[] =>
  readPendingQuestionClaimEntriesOrUnreadable() ?? [];

/**
 * `readPendingQuestionClaimEntriesInTab`, except that a read that THROWS answers `null` rather
 * than the empty list a tab with no entries answers. ONE read, so the two cannot disagree: a
 * second read to ask "did that throw" can answer cleanly after the first one threw. For the
 * caller that fails closed on an unreadable claim (`keepFiledPendingQuestionClaimEntriesInTab`).
 * A value that does not parse, or has another bundle's shape, is no claim to any reader, so it
 * stays an empty list here too.
 */
const readPendingQuestionClaimEntriesOrUnreadable = (): PendingQuestionClaimEntry[] | null => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_QUESTION_CLAIMS_KEY);
  } catch (err) {
    logger.warn('[pendingQuestion] sessionStorage unavailable for claim entries read', err);
    return null;
  }
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPendingQuestionClaimEntries(parsed) ? normalizeClaimEntries(parsed) : [];
  } catch (err) {
    logger.warn('[pendingQuestion] unreadable claim entries', err);
    return [];
  }
};

/**
 * THE SECRETS THE CLAIM POSTS, from the per-tab store first and native Preferences behind it.
 *
 * FIRST NON-EMPTY ANSWER WINS rather than a union of the two: the tab's copy is the CURRENT one
 * in this document, and Preferences is the app-kill recovery behind it. Unioning them would let a
 * previous flow's durable copy ride along with a claim the visitor has just agreed to.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this reads the committed store, which is NOT the same as
 * "consented". `/auth/link`'s mount effect hydrates the cross-tab mirror into
 * `PENDING_QUESTION_CLAIMS_KEY` unconditionally, before any gate is answered and on its no-gate
 * dead ends -- so a visitor who leaves the gate unanswered and then signs in another way in this
 * tab posts those secrets with no consent step. Only the SERVER delivery is really held until the
 * click. Holding hydrated entries in an offered-only key the ladder cannot read is the fix in the
 * spirit of the delivery path, and it spans `/auth/link`'s mount effect and gate read, so it is
 * not this module's to make alone.
 *
 * Async because the native leg is, and the same two-leg shape as
 * `hasPendingQuestionClaim` -- which is what keeps "there is a claim" and "here is what
 * it publishes" answering about the same store.
 */
export const readPendingQuestionClaimSecrets = async (): Promise<string[]> => {
  const inTab = readPendingQuestionClaimEntriesInTab();
  if (inTab.length > 0) {
    return claimSecretsForRequest(inTab);
  }
  if (isNativePlatform()) {
    return claimSecretsForRequest(await readNativePendingQuestionClaimEntries());
  }
  return [];
};

/**
 * Drops the per-tab flag, the entries AND the mirror. The web half of clearing a claim.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: moved here out of
 * `useClaimPendingQuestions.clearPendingQuestionClaim`, which now delegates to it
 * and adds native Preferences. `/auth/link`'s gate needs to CLEAR a claim it has
 * just named (the "Just sign me in" refusal), and importing that hook into the
 * screen would drag axios and react-query behind the one function it wants. The
 * split is the same one the mirror writers already live by: this module owns where
 * the claim lives, the hook owns the claim.
 *
 * The mirror goes with the rest rather than after it: it is what a later
 * `/auth/link` hydrates from, so leaving it would re-deliver a claim that was just
 * refused to the next link opened in this profile.
 */
export const clearPendingQuestionClaimInTab = (): void => {
  try {
    // ALL of them, by construction: this is the one clear that means "there is no claim here".
    for (const key of PENDING_QUESTION_TAB_KEYS) {
      sessionStorage.removeItem(key);
    }
  } catch (err) {
    logger.error('[pendingQuestion] Failed to clear sessionStorage:', err);
  }
  writeMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim, null);
};

/**
 * Drops the UNNAMED claim alone: the raw flag, and nothing a gate named.
 *
 * For `/auth/link`'s account-switch gates (review 5330705557, R13), which never offer the unnamed
 * scope (`carriesUnnamedClaim`; Dexter, 2026-09-29), so a flag standing there may belong to the
 * account being left. MAY, not does: `identity_timeout` and `confirm_unknown_identity` can arrive
 * with no other account to leave (a slow load times out for the right person too), and then the
 * drop discards the visitor's own unnamed ask. That is the ruled fail-safe direction, and the gate
 * tells them (`saysUnnamedClaimNotPosted` in `AuthLinkLanding.tsx`). Kept, the incoming account's
 * secrets retire reads it as owed and defers it into the marker a later gate offers
 * (`clearClaimedPendingQuestionSecrets`). Not `discardClaimInTab`, which also takes the entries and
 * the provenance bit the same click has just committed.
 */
export const dropUnnamedPendingQuestionClaimInTab = (): void => {
  try {
    sessionStorage.removeItem(PENDING_QUESTION_KEY);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to drop the unnamed claim:', err);
  }
};

/**
 * NARROWS THIS TAB'S CLAIM TO THE ENTRIES FILED UNDER `address`: `/auth/link`'s commit, which keeps
 * the claim to what its gate could name (`claimEntriesForRecipient`, an unfiled entry excluded), so
 * the sign-in presents no secret of a row filed under another address (Dexter, 2026-09-29).
 *
 * WHEN NO ENTRY IS LEFT BESIDE A RAW UNNAMED FLAG, THE FLAG IS DEFERRED rather than left standing.
 * The gate withheld the unnamed scope because the tab held entries, and with them gone the device
 * token would be the claim's only limb: it publishes every unclaimed row this browser wrote, the
 * dropped address's included, with no gate having offered that. So the flag becomes the marker a
 * later gate may offer on its own, through the flag-to-marker step the secrets retire also takes
 * (`deferOwedUnnamedClaimInTab`), web only for the reason given there. The sign-in ends where it
 * ended before the narrowing, less the other address's secret in its request.
 *
 * Returns whether the tab ended as intended: `false` when a claim is still standing after a write
 * that failed. The arm has already failed closed (`armClaimInTab`), the step's failure is logged
 * here, and the caller records the outcome.
 */
export const keepPendingQuestionClaimEntriesFiledUnder = (address: string): boolean => {
  const held = readPendingQuestionClaimEntriesInTab();
  const kept = claimEntriesForRecipient(held, address, { isFirstRecipient: false });
  if (kept.length === held.length) return true;
  if (kept.length > 0) return armClaimInTab(kept);
  // Nothing named is left: the entries and the provenance bit go with the flag, as
  // `discardClaimInTab` would take them, and the flag is deferred where it was standing.
  try {
    deferOwedUnnamedClaimInTab([PENDING_QUESTION_CLAIMS_KEY, PENDING_QUESTION_SOURCE_KEY]);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to defer the withheld unnamed claim:', err);
  }
  return !hasPendingQuestionClaimInTab();
};

/**
 * KEEPS THIS TAB'S CLAIM TO THE ENTRIES THAT CARRY AN ADDRESS, dropping every entry filed under
 * none: `/auth/link`'s mount, when the binding it took delivery of did not bind (F3 of #1253's
 * adversarial review, Copilot 4177018616).
 *
 * An entry filed under an address travels to that address and to no other
 * (`claimEntriesForRecipient`), so it needs no binding. An entry filed under NONE follows send
 * order instead, and a tab left with no binding reads any address as its first recipient: the
 * door's next send, to a stranger, bound the stranger first and carried that entry's secret, which
 * the flow read resolves to the question's title. The strip of the intent and the destination
 * (`holdStashToRule`) never reached the claim. Such an entry is dropped whole, as it would travel
 * whole: it has no plain half to keep.
 *
 * The unnamed flag is not an entry and stays as it was: it names no row and no send carries it,
 * and the gate that reads this tab next offers or withholds its scope from what is left.
 *
 * A CLAIM IT CANNOT READ IS DISCARDED, not kept. The ordinary reader answers a read that throws
 * with the empty list a tab with no entries answers, so this saw nothing unfiled, dropped nothing,
 * and the unfiled entries stayed in storage to travel once reads recovered. So a read that THREW
 * discards the whole claim (`discardClaimInTab`), filed
 * entries and the unnamed flag included. The flag is a separate key and could be told apart, but
 * discarding it too is the fail-closed answer: a claim the flow delivered comes back at the
 * confirm (`setDeliveredPendingQuestionClaimInTab`). A genuine empty read is still a no-op. The
 * same rule as the strip's for the intent and the destination (`holdStashToRule`).
 *
 * Returns whether the tab ended as intended, its filed entries kept and its flag untouched.
 * `false` has already failed closed (`armClaimInTab`, `discardClaimInTab`), and the caller records
 * it; an unreadable claim is always `false`.
 */
export const keepFiledPendingQuestionClaimEntriesInTab = (): boolean => {
  const held = readPendingQuestionClaimEntriesOrUnreadable();
  if (held === null) {
    discardClaimInTab();
    return false;
  }
  const filed = held.filter(entry => entry.filedUnder !== undefined);
  if (filed.length === held.length) return true;
  const owesUnnamedClaim = readUnnamedPendingQuestionClaimInTab();
  if (filed.length === 0 && !owesUnnamedClaim) {
    // Nothing left to keep: discard, rather than arm a claim of nothing.
    discardClaimInTab();
    return !hasPendingQuestionClaimInTab();
  }
  return armClaimInTab(filed);
};

/**
 * THE FLAG-TO-MARKER STEP, and the one copy of it: the raw unnamed flag goes, and where it was
 * standing the deferred marker takes its place, so only a gate can spend it.
 *
 * Three callers, each naming in `retiredWithTheFlag` what it retires in the same step. The secrets
 * retire (`clearClaimedPendingQuestionSecrets`) passes the named entries; `/auth/link`'s narrowing
 * (`keepPendingQuestionClaimEntriesFiledUnder`) passes the entries and the provenance bit, once no
 * entry is left; the door's redemption of an approval made elsewhere
 * (`deferUnnamedPendingQuestionClaimInTab`) passes nothing, because only the flag is its subject.
 * The keys go between the flag and the marker, the order the retire has always had, and it is the
 * fail-closed one: everything is removed BEFORE the marker is written, so a write refused part way
 * leaves no raw flag for `/callback` to spend.
 *
 * A MARKER ALREADY STANDING IS LEFT AS IT WAS, because nothing here removes it: the keys a caller
 * may retire are typed to exclude `PENDING_QUESTION_DEFERRED_KEY`. That is what keeps a second
 * pass in the same tab (a second consented claim, a redemption after a retire) from dropping a debt
 * whose flag an earlier pass had already turned into the marker.
 *
 * THROWS on a storage fault: each caller owns its `try` and the line it logs.
 */
const deferOwedUnnamedClaimInTab = (
  retiredWithTheFlag: readonly (
    typeof PENDING_QUESTION_CLAIMS_KEY | typeof PENDING_QUESTION_SOURCE_KEY
  )[],
): void => {
  // OWED MEANS THE RAW FLAG IS STANDING, asked BEFORE the removals below, because the flag is this
  // step's own first casualty and asking afterwards always answers no. A standing marker needs no
  // read here: the step never removes it (see the header), so it survives whatever this answers.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: the marker used to be written UNCONDITIONALLY, which
  // invented the obligation it exists to record. An ordinary post-secrets guest holds entries and
  // no flag, so their own successful claim left the marker standing and the next magic-link
  // sign-in IN THAT TAB was offered the UNNAMED SCOPE -- `CARRIED_CLAIM_PHRASES.unnamed` in
  // `AuthLinkLanding.tsx` -- a scope that tab was never owed. Continue there promotes the marker
  // to a real flag and the device-token limb publishes whatever this browser wrote, unnamed,
  // inside a flow whose gate had named something else. An unreadable store answers NO for the
  // same fail-closed reason the arm does: a stranded row survives to its TTL, an unconsented
  // publish does not come back.
  const owesUnnamedClaim = readUnnamedPendingQuestionClaimInTab();
  // THE RAW FLAG GOES, and where one was owed the DEFERRED marker replaces it. Leaving the flag
  // would hand the survivor to `useCallbackRedirect`'s unconditional claim on the next sign-in in
  // this tab, which has no consent step at all; only `/auth/link` reads the deferred value, and
  // only a Continue there promotes it back to a flag. See `PENDING_QUESTION_DEFERRED_KEY` /
  // `PENDING_QUESTION_DEFERRED_VALUE`.
  sessionStorage.removeItem(PENDING_QUESTION_KEY);
  for (const key of retiredWithTheFlag) {
    sessionStorage.removeItem(key);
  }
  // WEB ONLY, because the marker's only reader is a browser `/auth/link`. On native that screen
  // renders "Open this link in your browser" and nothing there can promote the marker, so
  // writing one would be state that survives the WebView session, can never be consented to,
  // and counts as a claim in `magic_link_landing_viewed` if the dead end is ever visited. The
  // secrets retire's native clear says native retires an owed claim UNATTEMPTED; this guard is
  // what makes that true.
  if (owesUnnamedClaim && !isNativePlatform()) {
    sessionStorage.setItem(PENDING_QUESTION_DEFERRED_KEY, PENDING_QUESTION_DEFERRED_VALUE);
  }
};

/**
 * A CONTINUATION REDEMPTION DOES NOT SPEND THIS TAB'S UNNAMED CLAIM: the raw flag becomes the
 * deferred marker, which only a gate may spend. Called wherever a sign-in is about to reach this
 * tab in a way that a raw flag would let `device_token` publish with no consent step:
 *
 * - as the first statement of the door's `redeem()`, its own redemption of an approval made
 *   elsewhere (`AuthDoor.tsx`, whose note there carries webapp #1187 and Dexter's decision of
 *   2026-09-26);
 * - at every door stand-down for a flow ANOTHER tab of this browser redeemed instead (the poll's
 *   `claimed`, the cross-tab broadcast, a claim answered `taken`), and in `AuthProvider`'s
 *   `handleSignedInInOtherTab` for every cross-tab SIGNED_IN delivered to a running tab, the one
 *   point a same-browser `/auth/link` redeeming in its own tab reaches. That sign-in reaches this
 *   tab as a cross-tab reload with no `/callback` of its own, so the flag would otherwise wait raw
 *   for the next interactive one here (D-08 of #1279's review). A sign-in that is not delivered
 *   (a tab not running at the broadcast, a missing or stale sign-in slot, the storage fallback
 *   without BroadcastChannel) still leaves it raw; that handler names the cases.
 *
 * ONLY THE FLAG MOVES, through the step the secrets retire uses (`deferOwedUnnamedClaimInTab`). The
 * named entries publish by secret, each bounded by the signing-in address; the delivery bit and the
 * mirror are not this step's subject. All three are left as they were. A tab owed nothing has
 * nothing written, and a marker already standing stays.
 *
 * Never throws: a storage fault is logged at `error`, the one level a production build keeps, and
 * the step's order means it has already taken the flag away if it got as far as the marker.
 */
export const deferUnnamedPendingQuestionClaimInTab = (): void => {
  // NATIVE IS EXEMPT, explicitly and first (Dexter, 2026-09-26). Converting the WebView flag does
  // not stop the native settle, which falls through to the Preferences marker every native ask
  // writes, and the step writes no deferred marker on native, so the call would only drop the flag
  // while the claim still publishes. Here rather than at each call, because the door's poll, one
  // of the callers, runs on native too.
  if (isNativePlatform()) return;
  try {
    deferOwedUnnamedClaimInTab([]);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to defer the unnamed claim:', err);
  }
};

/**
 * RETIRES EXACTLY WHAT A SECRETS-ONLY CLAIM COULD HAVE PUBLISHED: the named questions go, and an
 * unnamed claim this tab was OWED is converted into a deferred marker instead of being spent or
 * dropped.
 *
 * THE RAW FLAG DOES NOT SURVIVE THIS CALL. It is REMOVED, and a marker that only a gate may read is
 * written in its place WHEN ONE WAS OWED, which is when the flag was standing. A marker an earlier
 * writer left is kept as it was, because nothing here removes it. A tab that was owed nothing has
 * nothing written for it, because a marker there would offer the unnamed scope to a visitor who was
 * never owed it. The notes in the shared step (`deferOwedUnnamedClaimInTab`) state the rest; this
 * header agrees with them rather than the reverse.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the success path used to clear everything, and that
 * silently dropped rows nothing had attempted. A visitor can hold BOTH kinds of claim at once
 * during the transition -- pre-secrets rows written on this browser profile, which have only
 * the legacy flag, and secrets delivered from another context. The claim sends the secrets
 * ALONE (the device token is the fallback limb, never a companion one), so the pre-secrets
 * rows were never presented; wiping the flag on that success retired them without ever trying,
 * which is the feature's own original same-device population.
 *
 * So an OWED unnamed claim is offered to the next MAGIC-LINK REDEMPTION IN THIS SAME TAB, and only
 * to that: the marker's only reader is `/auth/link`'s mount, it is per-tab, it is never mirrored
 * or sent, and an explicit sign-out in this tab or another sweeps it. Sending a second token-only
 * request inside the same sign-in would publish unnamed rows inside a consented flow, which is the
 * one thing this whole design forbids, so the marker is the only place the obligation can go from
 * here.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the marker's writers and that reader almost never meet, so
 * "deferred to a gate" is not "kept". This retire writes the marker AFTER a sign-in, and a
 * landing's Continue (`keepPendingQuestionClaimEntriesFiledUnder`) and the door's redemption of an
 * approval made elsewhere (`deferUnnamedPendingQuestionClaimInTab`) write it just BEFORE one, so
 * either way only a LATER redemption in the same tab can read it, and magic links open in a NEW
 * tab; an ordinary sign-in ignores the marker by design, and a sign-out removes it, made in this
 * tab or in another (`AuthProvider`'s `handleSignedOutInOtherTab` clears this tab's claim beside
 * its overlay intents).
 * The realistic survivor is an installed PWA reusing its window for a SECOND redemption. That
 * loss is ACCEPTED: Dexter cancelled TECH-509, with TECH-534, on 2026-09-23. Neither way out was
 * taken (retiring the unnamed scope outright, or making it consentable where the visitor actually
 * is, a post-sign-in prompt on the ordinary path, web and native), and adding one would reopen
 * that ruling.
 *
 * The mirror goes with the entries: it is a copy of them, and a survivor would re-deliver a
 * claim that has already been published.
 */
export const clearClaimedPendingQuestionSecrets = (): void => {
  // WHAT THE REQUEST ACTUALLY PRESENTED, read through the SAME expression the send uses
  // (`readPendingQuestionClaimSecrets`'s tab leg) and read BEFORE the removals below, because it is
  // what the native retire is scoped by. An empty answer here is not "nothing was sent": it means
  // the send fell through to the durable list, which is then what the settle retires in full.
  const presentedSecrets = claimSecretsForRequest(readPendingQuestionClaimEntriesInTab());
  try {
    // The named entries go WITH the flag, between it and any marker: the order this retire has
    // always had, now the shared step's (see `deferOwedUnnamedClaimInTab`).
    deferOwedUnnamedClaimInTab([PENDING_QUESTION_CLAIMS_KEY]);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to defer the remaining claim:', err);
  }
  writeMagicLinkMirror(MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim, null);
  if (isNativePlatform()) {
    // SCOPED BY WHAT WAS SENT, not by the key. There is no native deferred key, because the
    // marker's only reader is a browser `/auth/link`: on native that screen is a dead end that
    // hands off to a browser, so a marker written there could never be consented to. So an owed
    // UNNAMED claim is still retired here UNATTEMPTED rather than spent ungated or deferred to a
    // gate it can never reach. That is the deliberate choice between two bad options, and it is
    // about the FLAG.
    //
    // The named entries are a different fact, and the durable list is no longer a copy of the
    // tab's: `mergeNativePendingQuestionClaim` makes it the UNION, so it holds asks from before
    // an app kill that the tab-first send never presented. Clearing the key wholesale retired
    // those unsent, which is the one thing this whole retire exists to prevent.
    void retireNativePendingQuestionClaim(presentedSecrets);
  }
};

/**
 * RETIRES THE NATIVE DURABLE ENTRIES THE REQUEST PRESENTED, and leaves the ones it did not.
 *
 * The durable list is the UNION of every ask this install made (`mergeNativePendingQuestionClaim`)
 * while the send is TAB-FIRST (`readPendingQuestionClaimSecrets`), so the two are only equal while
 * the tab still holds everything. After an app kill they diverge, and the difference is precisely
 * "asks this device made that this WebView session no longer knows about". Those were never
 * presented, so a settle has nothing to say about them.
 *
 * AN EMPTY `presentedSecrets` MEANS THE DURABLE LIST WAS ITSELF THE BODY: the tab leg answered
 * nothing, the send fell through to Preferences, and every row in it was attempted. So that case
 * clears both keys, exactly as before -- which is also what keeps this from degenerating into
 * "never clear native".
 *
 * A SURVIVING REMAINDER KEEPS THE MARKER, by writing it back with its entries. That is safe for
 * the reason the wholesale clear was needed: the hazard of a standing marker is an ENTRY-LESS one,
 * which makes `claimSecrets` empty and sends the ungated `device_token` limb over every row this
 * browser wrote. A marker standing WITH entries sends those rows by name, each bounded by the
 * backend's `lower(email) == lower(user email)` conjunct on the secret limb.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this bounds the LOSS, not the delivery. The remainder now
 * survives the settle instead of being destroyed unsent, and the next sign-in in this install
 * presents it (the tab is empty by then, so the send's durable leg reads it); it does not make a
 * second request inside the same sign-in. Who claims a row whose asker never signs in again
 * was TECH-509's question; Dexter cancelled it, with TECH-534, on 2026-09-23 and accepted that
 * loss.
 */
const retireNativePendingQuestionClaim = async (
  presentedSecrets: readonly string[],
): Promise<void> => {
  if (presentedSecrets.length === 0) {
    await clearNativePendingQuestionClaim();
    return;
  }
  const remaining = (await readNativePendingQuestionClaimEntries()).filter(
    entry => !presentedSecrets.includes(entry.secret),
  );
  if (remaining.length === 0) {
    await clearNativePendingQuestionClaim();
    return;
  }
  await storeNativePendingQuestionClaim(remaining);
};

/**
 * Stores the claim in native Capacitor Preferences, which survives the app being killed
 * during the auth flow where sessionStorage does not.
 *
 * TWO KEYS, the marker and the entries, for the same reason the per-tab store has two:
 * the marker keeps its exact legacy value so an app killed mid-auth on the previous
 * bundle still reads back as "a claim is pending", and the entries are additive beside
 * it. An empty list REMOVES the entries key rather than writing `[]`, so a durable copy
 * never outlives the questions it named.
 *
 * `entries` IS REQUIRED, with no `[]` default: the default made a no-argument call mean "set the
 * marker and REMOVE the entries", which is the base's call shape with new and much worse meaning --
 * the claim survives as a device-token sweep with nothing named. Callers say what they are storing.
 *
 * MODULE-PRIVATE: it REPLACES the durable list, so its only callers are the two that decide what
 * the whole list should be, the merge (`mergeNativePendingQuestionClaim`) and the retire
 * (`retireNativePendingQuestionClaim`). A second ask after an app kill once replaced the list and
 * lost the first ask's secret; the merge is the fix, and an exported replace would be a way around
 * it.
 *
 * EACH ENTRY IS WRITTEN WITHOUT THE ADDRESS IT WAS FILED UNDER, and this is the one place that
 * writes the list, so the merge and the retire both go through it. A keep-list of the three fields
 * the list's readers use: the request and the retire read the secret, the cap sorts by the ask
 * time, and the store's shape check wants a title. Why the address stays out is the note on
 * `PendingQuestionClaimEntry.filedUnder`. A keep-list rather than dropping that one field, so a
 * field added to the entry later stays off this store unless it is added here on purpose. A list
 * that already carries addresses (a build that kept them wrote it) keeps them until a merge or a
 * retire rewrites it, or a clear (sign-out among them) removes it.
 */
const storeNativePendingQuestionClaim = async (
  entries: readonly PendingQuestionClaimEntry[],
): Promise<void> => {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    // DISPATCHED TOGETHER, awaited once. The two keys are independent, so serialising them only
    // spent an extra bridge round-trip and widened the window in which the durable MARKER exists
    // without its entries -- an app killed in that gap relaunches claiming by device token or by
    // an older list. Two writes are never atomic, so this narrows the window rather than closing
    // it; the array order is the dispatch order.
    await Promise.all([
      Preferences.set({ key: NATIVE_PENDING_QUESTION_KEY, value: PENDING_QUESTION_VALUE }),
      entries.length > 0
        ? Preferences.set({
            key: NATIVE_PENDING_QUESTION_CLAIMS_KEY,
            value: JSON.stringify(
              entries.map(({ secret, title, askedAt }) => ({ secret, title, askedAt })),
            ),
          })
        : Preferences.remove({ key: NATIVE_PENDING_QUESTION_CLAIMS_KEY }),
    ]);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to store native Preferences:', err);
  }
};

/** Whether native Preferences is holding a claim marker. Callers gate on `isNativePlatform`. */
export const hasNativePendingQuestionClaim = async (): Promise<boolean> => {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: NATIVE_PENDING_QUESTION_KEY });
    return value === PENDING_QUESTION_VALUE;
  } catch (err) {
    logger.error('[pendingQuestion] Failed to read native Preferences:', err);
    return false;
  }
};

/**
 * The native DURABLE list: every ask this install made, merged at each create
 * (`mergeNativePendingQuestionClaim`) and kept across an app kill, each entry without the address
 * it was filed under (`storeNativePendingQuestionClaim`). Callers gate on `isNativePlatform`.
 *
 * IT NEVER REJECTS: a read the bridge fails, or a value that does not parse, answers the empty
 * list, and the failure is recorded here at `error`. Every caller awaits it bare (the claim's
 * send, the merge and the retire), so a rejection would fail the read of what a claim presents
 * instead of leaving it empty.
 */
const readNativePendingQuestionClaimEntries = async (): Promise<PendingQuestionClaimEntry[]> => {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: NATIVE_PENDING_QUESTION_CLAIMS_KEY });
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    return isPendingQuestionClaimEntries(parsed) ? normalizeClaimEntries(parsed) : [];
  } catch (err) {
    logger.error('[pendingQuestion] Failed to read native claim entries:', err);
    return [];
  }
};

/** Drops both native Preferences keys. Callers gate on `isNativePlatform`. */
export const clearNativePendingQuestionClaim = async (): Promise<void> => {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    // Dispatched together, for the reason on the store above -- here the window is the reverse
    // one, entries surviving a removed marker.
    await Promise.all([
      Preferences.remove({ key: NATIVE_PENDING_QUESTION_KEY }),
      Preferences.remove({ key: NATIVE_PENDING_QUESTION_CLAIMS_KEY }),
    ]);
  } catch (err) {
    logger.error('[pendingQuestion] Failed to clear native Preferences:', err);
  }
};
